import React, { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { FiCalendar, FiChevronRight } from "react-icons/fi";
import { addDays } from "date-fns";
import { Transition } from "@headlessui/react";
import moment from "moment";
import { usePopper } from "react-popper";
import useMobileCheck from "../../../../Hooks/useMobileCheck";

function DateRangePicker({ label = "", range, setRange = () => {}, className = "", inputClass = "", minDate, maxDate, backDateSelection = false }) {
	const safeRange = range || {};
	const { isMobile } = useMobileCheck();
	const nativeRange = {
		startDate: safeRange.startDate ? new Date(safeRange.startDate) : new Date(),
		endDate: safeRange.endDate ? new Date(safeRange.endDate) : addDays(new Date(), 7),
		key: "selection",
	};

	const [ShowPicker, setShowPicker] = useState(false);
	const layout = useMemo(
		() => ({
			months: isMobile ? 1 : 2,
			direction: isMobile ? "vertical" : "horizontal",
		}),
		[isMobile]
	);

	//Popper stuff
	const [PopRef, setPopRef] = useState(null);
	const [PopElem, setPopElem] = useState(null);
	const { styles, attributes, update } = usePopper(PopRef, PopElem, {
		placement: isMobile ? "bottom" : "bottom-start",
		strategy: isMobile ? "fixed" : "absolute",
		modifiers: [
			{ name: "offset", options: { offset: [0, 8] } },
			{ name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
			{ name: "flip", options: { fallbackPlacements: ["top", "bottom"] } },
		],
	});

	// Close on outside click / touch and Esc
	useEffect(() => {
		if (!ShowPicker) return;
		const onPointer = (e) => {
			const target = e.target;
			if (!target) return;
			if (PopRef && PopRef.contains(target)) return;
			if (PopElem && PopElem.contains(target)) return;
			setShowPicker(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setShowPicker(false);
		};
		document.addEventListener("mousedown", onPointer, true);
		document.addEventListener("touchstart", onPointer, true);
		document.addEventListener("keydown", onKey, true);
		return () => {
			document.removeEventListener("mousedown", onPointer, true);
			document.removeEventListener("touchstart", onPointer, true);
			document.removeEventListener("keydown", onKey, true);
		};
	}, [ShowPicker, PopRef, PopElem]);

	// Fix first-open misalignment by forcing Popper recalculation after mount
	useEffect(() => {
		if (!ShowPicker) return;
		if (!update) return;
		let raf1 = requestAnimationFrame(() => {
			update();
			requestAnimationFrame(() => update());
		});
		return () => cancelAnimationFrame(raf1);
	}, [ShowPicker, update]);

	return (
		<div className={`relative ${className}`}>
			{label && <label className="block mb-2">{label}</label>}
			<div ref={setPopRef} className={`text-sm ring-1 ring-inset ring-muted bg-canvas shadow-sm rounded-md p-2.5 align-center cursor-pointer ${inputClass}`} onClick={() => setShowPicker(!ShowPicker)}>
				{safeRange.startDate && safeRange.endDate ? (
					<>
						<FiCalendar className="mr-2" />
						<div className="whitespace-nowrap">{moment(nativeRange.startDate).format("DD MMM YY")}</div>
						<FiChevronRight className="ml-1 mr-1" />
						<div className="whitespace-nowrap">{moment(nativeRange.endDate).format("DD MMM YY")}</div>
					</>
				) : (
					"Select Dates"
				)}
			</div>
			<Transition ref={setPopElem} style={styles.popper} {...attributes.popper} className="z-20" show={ShowPicker ? true : false}>
				<DateRange
					className="overflow-hidden border rounded-lg shadow-lg max-w-[95vw]"
					editableDateInputs={true}
					onChange={(item) => {
						const { startDate, endDate } = item.selection;
						setRange({
							startDate: moment(startDate).valueOf(),
							endDate: moment(endDate).valueOf(),
						});
						if (!moment(startDate).isSame(moment(endDate))) {
							setShowPicker(false);
						}
					}}
					minDate={minDate ? moment(minDate).toDate() : !backDateSelection ? new Date() : undefined}
					maxDate={maxDate ? moment(maxDate).toDate() : moment().add(1, "year").toDate()}
					moveRangeOnFirstSelection={false}
					ranges={[nativeRange]}
					months={layout.months}
					direction={layout.direction}
					rangeColors={["#be3735"]}
				/>
			</Transition>
		</div>
	);
}

export default DateRangePicker;
